import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Button from '@material-ui/core/Button';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import Paper from '~/components/Paper';
import Table from '~/components/Table/Table';
import TablePagination from '~/components/Table/TablePagination';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import InputControl from '~/components/Input/InputControl';
import InputSelect from '~/components/Input/InputSelect';
import Request from '~/providers/Request';
import useStyles from './styles';

const PATH = '/admin/pre_registration_campaign/invitations';
const ALL_STATUSES = 'all';
const STATUSES = ['sent', 'delivered', 'undeliverable', 'failed', 'skipped'];
const COLUMNS = ['name', 'phone', 'status', 'step', 'reason', 'validation_questions', 'sent_at', 'delivered_at', 'validated_at'];
const PER_PAGE_DEFAULT = 20;

function Invitations({ version }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const tr = (key, options) => t(`preRegistrationCampaign.extras.${key}`, options);
  const trAttr = (attr) => t(`preRegistrationCampaign.attributes.${attr}`);
  const label = (group, value) => (value ? tr(`${group}.${value}`, { defaultValue: value }) : '');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(PER_PAGE_DEFAULT);
  const [status, setStatus] = useState(ALL_STATUSES);
  const [reloads, setReloads] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, per_page: perPage, ...(status !== ALL_STATUSES ? { status } : {}) };
      const response = await Request.get(PATH, params, { normalizeJSON: false });
      setRows(response.data || []);
      setTotal(Number((response.meta && response.meta.total) || 0));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, status]);

  useEffect(() => {
    load();
  }, [load, version, reloads]);

  const formatDate = (value) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : '');
  const questions = (value) => `${value || ''}`.split(',').filter(Boolean).map((question) => label('questions', question)).join(', ');
  const reason = (row) => {
    if (row.failed_reason) return label('reasons', row.failed_reason);
    if (row.skip_reason) return label('reasons', row.skip_reason);
    return row.error || '';
  };

  const statusOptions = [
    { id: ALL_STATUSES, name: tr('statusAll') },
    ...STATUSES.map((value) => ({ id: value, name: tr(`statuses.${value}`) })),
  ];

  return (
    <Paper
      Icon={WhatsAppIcon}
      iconColor="green"
      title={tr('invitationsTitle')}
      actions={(
        <div className={classes.paperActions}>
          <Button
            variant="outlined"
            size="small"
            className={classes.smallButton}
            disabled={loading}
            onClick={() => setReloads((count) => count + 1)}
          >
            {tr('reload')}
          </Button>
        </div>
      )}
    >
      <div className={classes.filterRow}>
        <GridContainer>
          <GridItem xs={12} md={4}>
            <InputControl
              name="status"
              value={status}
              label={tr('statusFilter')}
              inputComponent={InputSelect}
              onChange={({ status: selected }) => {
                setStatus(selected || ALL_STATUSES);
                setPage(0);
              }}
              InputProps={{ options: statusOptions, outValueType: 'string' }}
            />
          </GridItem>
        </GridContainer>
      </div>

      <Table
        size="small"
        loading={loading}
        emptyData={rows.length === 0}
        columns={COLUMNS.map((column) => <TableCell key={column} className={classes.headCell}>{trAttr(column)}</TableCell>)}
      >
        {rows.map((row) => (
          <TableRow key={row.id} hover className={classes.row}>
            <TableCell>{row.name}</TableCell>
            <TableCell>{row.phone}</TableCell>
            <TableCell>{label('statuses', row.status)}</TableCell>
            <TableCell>{label('steps', row.step)}</TableCell>
            <TableCell>{reason(row)}</TableCell>
            <TableCell>{questions(row.validation_questions)}</TableCell>
            <TableCell>{formatDate(row.sent_at)}</TableCell>
            <TableCell>{formatDate(row.delivered_at)}</TableCell>
            <TableCell>{formatDate(row.validated_at)}</TableCell>
          </TableRow>
        ))}
      </Table>

      <TablePagination
        count={total}
        rowsPerPage={perPage}
        page={page}
        onChangePage={(event, newPage) => setPage(newPage)}
        onChangeRowsPerPage={(event) => {
          setPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  );
}

export default React.memo(Invitations);
